import React, { useCallback, useEffect, useState } from 'react';
import { AppLayout } from '../../components/layout/AppLayout';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import {
  GoogleAddressAutocomplete,
  GoogleAddressSelection,
} from '../../components/ui/GoogleAddressAutocomplete';
import { useToast } from '../../components/ui/Toast';
import { profileApi, VendorProfile } from '../../api/earnings';
import { fileToBase64DataUri, validateImageFile } from '../../api/uploads';

// Accepts +, digits, spaces, dashes, parens — mirrors the backend regex
// (^[0-9+][0-9\s()-]{6,19}$).
const PHONE_RE = /^[0-9+][0-9\s()-]{6,19}$/;

const STATUS_VARIANT: Record<
  VendorProfile['status'],
  'success' | 'warning' | 'danger'
> = {
  active: 'success',
  pending: 'warning',
  suspended: 'danger',
};

export const Profile: React.FC = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<VendorProfile | null>(null);

  // Editable fields.
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [tagline, setTagline] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [freeDelivery, setFreeDelivery] = useState(false);
  const [freeDeliveryMinOrderAmount, setFreeDeliveryMinOrderAmount] =
    useState('0');
  const [freeDeliveryMaxSubsidyAmount, setFreeDeliveryMaxSubsidyAmount] =
    useState('0');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [locationTouched, setLocationTouched] = useState(false);
  const [addressSelection, setAddressSelection] =
    useState<GoogleAddressSelection | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [featuredUploading, setFeaturedUploading] = useState(false);

  const hydrate = useCallback((p: VendorProfile) => {
    setProfile(p);
    setName(p.name ?? '');
    setPhone(p.ownerPhone ?? '');
    setLocation(p.location ?? '');
    setTagline(p.tagline ?? '');
    setSpecialty(p.specialty ?? '');
    setFreeDelivery(p.freeDelivery);
    setFreeDeliveryMinOrderAmount(String(Number(p.freeDeliveryMinOrderAmount ?? 0)));
    setFreeDeliveryMaxSubsidyAmount(String(Number(p.freeDeliveryMaxSubsidyAmount ?? 0)));
    setLocationTouched(false);
    setAddressSelection(null);
    setAddressError(null);
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      hydrate(await profileApi.get());
    } catch (err) {
      toast.error(err, 'Failed to load your profile.');
    } finally {
      setLoading(false);
    }
  }, [toast, hydrate]);

  const handleAvatarChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = ''; // allow re-selecting the same file
      if (!file) return;
      const validationError = validateImageFile(file);
      if (validationError) {
        toast.info(validationError);
        return;
      }
      try {
        setAvatarUploading(true);
        const dataUri = await fileToBase64DataUri(file);
        const updated = await profileApi.uploadAvatar(dataUri);
        setProfile(updated);
        toast.success('Store logo updated.');
      } catch (err) {
        toast.error(err, 'Could not upload the image.');
      } finally {
        setAvatarUploading(false);
      }
    },
    [toast],
  );

  const handleFeaturedChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = ''; // allow re-selecting the same file
      if (!file) return;
      const validationError = validateImageFile(file);
      if (validationError) {
        toast.info(validationError);
        return;
      }
      try {
        setFeaturedUploading(true);
        const dataUri = await fileToBase64DataUri(file);
        const updated = await profileApi.uploadFeaturedImage(dataUri);
        setProfile(updated);
        toast.success('Featured image updated.');
      } catch (err) {
        toast.error(err, 'Could not upload the image.');
      } finally {
        setFeaturedUploading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    void fetchProfile();
  }, [fetchProfile]);

  const phoneError =
    phoneTouched && !PHONE_RE.test(phone.trim())
      ? phone.trim()
        ? 'Enter a valid phone number.'
        : 'Phone number is required.'
      : null;

  const phoneValid = PHONE_RE.test(phone.trim());

  async function handleSave() {
    setPhoneTouched(true);
    if (!phoneValid) {
      toast.error('A valid phone number is required to save your profile.');
      return;
    }
    if (locationTouched && location.trim() && !addressSelection) {
      setAddressError('Select a Google address from the suggestions.');
      toast.error('Select a validated Google address before saving.');
      return;
    }
    try {
      setSaving(true);
      const updated = await profileApi.update({
        ownerPhone: phone.trim(),
        name: name.trim() || undefined,
        location: addressSelection?.formattedAddress ?? (location.trim() || null),
        ...(addressSelection
          ? {
              pickupAddressLine: addressSelection.addressLine,
              pickupCity: addressSelection.city,
              pickupState: addressSelection.state,
              pickupPhone: phone.trim(),
              pickupLat: addressSelection.lat,
              pickupLng: addressSelection.lng,
              pickupGooglePlaceId: addressSelection.placeId,
            }
          : {}),
        tagline: tagline.trim() || null,
        specialty: specialty.trim() || null,
        freeDelivery,
        freeDeliveryMinOrderAmount: freeDelivery
          ? Math.max(0, Number(freeDeliveryMinOrderAmount || 0))
          : 0,
        freeDeliveryMaxSubsidyAmount: freeDelivery
          ? Math.max(0, Number(freeDeliveryMaxSubsidyAmount || 0))
          : 0,
      });
      hydrate(updated);
      toast.success('Profile saved.');
    } catch (err) {
      toast.error(err, 'Failed to save your profile.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppLayout>
      <PageHeader
        title="Profile"
        description="Manage your store details. A phone number is required — couriers use it to coordinate pickups."
      />

      {loading || !profile ? (
        <Card>
          <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}>
            <Spinner />
          </div>
        </Card>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 16,
            maxWidth: 640,
          }}
        >
          {/* Store logo */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              {profile.avatarUrl ? (
                <img
                  src={profile.avatarUrl}
                  alt="Store logo"
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '1px solid var(--neutral-300)',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    background: 'var(--blue-600)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 28,
                    fontWeight: 700,
                  }}
                >
                  {(profile.name?.[0] ?? '?').toUpperCase()}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 200 }}>
                <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Store logo</h2>
                <p style={{ fontSize: 13, color: 'var(--neutral-500)', margin: '4px 0 10px' }}>
                  Shown on your storefront across the PayIQ app. JPG, PNG or WebP, up to 5MB.
                </p>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 42,
                    padding: '0 18px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--neutral-300)',
                    color: 'var(--neutral-800)',
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: avatarUploading ? 'not-allowed' : 'pointer',
                    opacity: avatarUploading ? 0.6 : 1,
                  }}
                >
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleAvatarChange}
                    disabled={avatarUploading}
                    style={{ display: 'none' }}
                  />
                  {avatarUploading
                    ? 'Uploading…'
                    : profile.avatarUrl
                      ? 'Change logo'
                      : 'Upload logo'}
                </label>
              </div>
            </div>
          </Card>

          {/* Featured storefront image */}
          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Featured image</h2>
                <p style={{ fontSize: 13, color: 'var(--neutral-500)', margin: '4px 0 0' }}>
                  The wide storefront image shown on your seller card in the PayIQ
                  shop. A landscape photo of your store or products works best. JPG,
                  PNG or WebP, up to 5MB.
                </p>
              </div>
              {profile.featuredImageUrl ? (
                <img
                  src={profile.featuredImageUrl}
                  alt="Featured storefront"
                  style={{
                    width: '100%',
                    maxWidth: 420,
                    aspectRatio: '3 / 2',
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--neutral-300)',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    maxWidth: 420,
                    aspectRatio: '3 / 2',
                    borderRadius: 'var(--radius-md)',
                    border: '1px dashed var(--neutral-300)',
                    background: 'var(--neutral-100)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--neutral-500)',
                    fontSize: 13,
                  }}
                >
                  No featured image yet
                </div>
              )}
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'flex-start',
                  height: 42,
                  padding: '0 18px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--neutral-300)',
                  color: 'var(--neutral-800)',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: featuredUploading ? 'not-allowed' : 'pointer',
                  opacity: featuredUploading ? 0.6 : 1,
                }}
              >
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFeaturedChange}
                  disabled={featuredUploading}
                  style={{ display: 'none' }}
                />
                {featuredUploading
                  ? 'Uploading…'
                  : profile.featuredImageUrl
                    ? 'Change featured image'
                    : 'Upload featured image'}
              </label>
            </div>
          </Card>

          {/* Read-only account summary */}
          <Card>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
              <Field label="Store">{profile.name}</Field>
              <Field label="Email">{profile.ownerEmail ?? '—'}</Field>
              <Field label="Status">
                <Badge variant={STATUS_VARIANT[profile.status]}>
                  {profile.status}
                </Badge>
              </Field>
            </div>
          </Card>

          {/* Editable business profile */}
          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h2 style={{ fontSize: 16, fontWeight: 600 }}>Business details</h2>

              <Input
                label="Store name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your store name"
                maxLength={80}
              />

              <Input
                label="Phone number *"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onBlur={() => setPhoneTouched(true)}
                placeholder="e.g. 08012345678"
                inputMode="tel"
                error={phoneError}
                hint={
                  phoneError
                    ? undefined
                    : 'Required. Used by couriers to reach you for pickup.'
                }
              />

              <GoogleAddressAutocomplete
                label="Location"
                value={location}
                placeholder="Start typing your pickup address"
                error={addressError}
                onChange={(value) => {
                  setLocation(value);
                  setLocationTouched(true);
                  setAddressSelection(null);
                  setAddressError(
                    value.trim()
                      ? 'Select a Google address from the suggestions.'
                      : null,
                  );
                }}
                onSelect={(selection) => {
                  setLocation(selection.formattedAddress);
                  setLocationTouched(true);
                  setAddressSelection(selection);
                  setAddressError(null);
                }}
              />

              <Input
                label="Tagline"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="A short line about your store"
                maxLength={160}
              />

              <Input
                label="Storefront type"
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                placeholder="e.g. Electronics, Pharmacy, Fashion"
                hint="Shown as a pill on your seller card in the shop."
                maxLength={40}
              />

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 14,
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={freeDelivery}
                  onChange={(e) => setFreeDelivery(e.target.checked)}
                />
                Offer free delivery on my products
              </label>

              {freeDelivery ? (
                <>
                  <Input
                    label="Minimum order amount"
                    type="number"
                    min={0}
                    step={100}
                    value={freeDeliveryMinOrderAmount}
                    onChange={(e) => setFreeDeliveryMinOrderAmount(e.target.value)}
                    placeholder="0"
                    hint="Set 0 to offer delivery support on every order."
                  />
                  <Input
                    label="Maximum delivery subsidy"
                    type="number"
                    min={0}
                    step={100}
                    value={freeDeliveryMaxSubsidyAmount}
                    onChange={(e) => setFreeDeliveryMaxSubsidyAmount(e.target.value)}
                    placeholder="0"
                    hint="Set 0 for no explicit cap. PayIQ still never deducts more than the order subtotal; buyers pay any uncovered courier fee."
                  />
                </>
              ) : null}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                  marginTop: 4,
                }}
              >
                <Button onClick={handleSave} loading={saving} disabled={!phoneValid}>
                  Save changes
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </AppLayout>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div
      style={{
        fontSize: 12,
        color: 'var(--text-muted)',
        marginBottom: 4,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
      {children}
    </div>
  </div>
);
